import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { FeedbackTopicsService } from './feedbackTopicsApi';

type TopicDoc = {
  _id: string;
  name?: string;
  tripId?: string;
  userId?: string;
  companySub?: string;
  inputFields?: {
    category?: string | null;
    subCategory?: string | null;
    [k: string]: any;
  };
  category?: string | null;
  subCategory?: string | null;
  createdAt?: string;
};

type Taxonomy = {
  categories: string[];
  subcategories: Record<string, string[]>;
};

type SearchPayload = {
  pageNumber: number;
  pageSize: number;
  category?: string;
  subCategory?: string;
  allCategories?: boolean;
  allSubcategories?: boolean;
  unassignedOnly?: boolean;
  tripId?: string;
  userId?: string;
  trainManagerId?: string;
  crewMemberId?: string;
  startDate?: string;
  endDate?: string;
};

type SearchResult = {
  data: TopicDoc[];
  total: number;
  page: number;
  size: number;
};

type AssignmentStats = {
  total: number;
  categories: { category: string | null; count: number; pct: number }[];
  subcategories: {
    category: string | null;
    subCategory: string | null;
    count: number;
    pctOfCategory: number;
    pctOfTotal: number;
  }[];
  unassigned: { count: number; pct: number };
};

// Stats fields shared at every level (overall, category, subcategory)
export type ExecStatsFields = {
  total: number;
  totalByStaff: number;
  totalByGuest: number;
  totalPositiveSentiment: number;
  totalNegativeSentiment: number;
  totalNeutralSentiment: number;
  positiveSentimentPct: number;
  negativeSentimentPct: number;
  neutralSentimentPct: number;
  totalAllocatedAndAcknowledged: number;
  totalAllocatedButNotAcknowledged: number;
  totalUnallocated: number;
  totalAllocatedAndAcknowledgedPct: number;
  totalAllocatedButNotAcknowledgedPct: number;
  totalUnallocatedPct: number;
};

export type ExecSubcategoryStats = ExecStatsFields & {
  subCategory: string | null;
};

export type ExecCategoryStats = ExecStatsFields & {
  category: string;
  subcategories: ExecSubcategoryStats[];
};

export type ExecByCategoryData = {
  overall: ExecStatsFields;
  categories: ExecCategoryStats[];
  unassigned: ExecStatsFields & { category: null };
};

interface IFeedbackTopicsState {
  // taxonomy
  taxonomy?: Taxonomy | null;
  taxonomyLoading: boolean;
  taxonomyError?: string | null;

  // search
  topics?: TopicDoc[];
  total?: number;
  page?: number;
  size?: number;
  searchLoading: boolean;
  searchError?: string | null;

  // stats
  stats?: AssignmentStats | null;
  statsLoading: boolean;
  statsError?: string | null;

  // exec by category
  execByCategory?: ExecByCategoryData | null;
  execByCategoryLoading: boolean;
  execByCategoryError?: string | null;
}

const initialState: IFeedbackTopicsState = {
  taxonomy: null,
  taxonomyLoading: false,
  taxonomyError: null,

  topics: [],
  total: 0,
  page: 1,
  size: 10,
  searchLoading: false,
  searchError: null,

  stats: null,
  statsLoading: false,
  statsError: null,

  execByCategory: null,
  execByCategoryLoading: false,
  execByCategoryError: null,
};

export const tryToFetchTopicTaxonomy = createAsyncThunk<any, void>(
  'feedbackTopics/tryToFetchTopicTaxonomy',
  async () => {
    const result = await FeedbackTopicsService.tryToFetchTopicTaxonomy();
    // backend returns { success: true, data: ... }
    return result?.data?.data;
  }
);

export const tryToSearchFeedbackTopics = createAsyncThunk<any, SearchPayload>(
  'feedbackTopics/tryToSearchFeedbackTopics',
  async (args: SearchPayload) => {
    const result = await FeedbackTopicsService.tryToSearchFeedbackTopics(args);
    // backend returns { success: true, data: { data, total, page, size } }
    return result?.data?.data as SearchResult;
  }
);

export const tryToGetFeedbackTopicStats = createAsyncThunk<any, Omit<SearchPayload, 'pageNumber' | 'pageSize'> | undefined>(
  'feedbackTopics/tryToGetFeedbackTopicStats',
  async (filters) => {
    const result = await FeedbackTopicsService.tryToGetFeedbackTopicStats(filters);
    // backend returns { success: true, data: ... }
    return result?.data?.data as AssignmentStats;
  }
);

export const tryToFetchFeedbackExecByCategory = createAsyncThunk<
  ExecByCategoryData,
  Record<string, any> | undefined
>(
  'feedbackTopics/tryToFetchFeedbackExecByCategory',
  async (data, { rejectWithValue }) => {
    try {
      const result = await FeedbackTopicsService.tryToFetchFeedbackExecByCategory(data);
      return result?.data?.data as ExecByCategoryData;
    } catch (err) {
      const e = err as AxiosError<any>;
      return rejectWithValue(e?.response?.data?.message || e.message);
    }
  }
);

export const feedbackTopicsSlice = createSlice({
  name: 'feedbackTopics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // taxonomy
      .addCase(tryToFetchTopicTaxonomy.pending, (state) => {
        state.taxonomyLoading = true;
        state.taxonomyError = null;
      })
      .addCase(tryToFetchTopicTaxonomy.rejected, (state, action) => {
        state.taxonomyLoading = false;
        const err = action.error as unknown as AxiosError;
        state.taxonomyError = (err?.message as string) || 'Failed to load taxonomy';
      })
      .addCase(tryToFetchTopicTaxonomy.fulfilled, (state, action) => {
        state.taxonomyLoading = false;
        state.taxonomy = action.payload || null;
      })

      // search
      .addCase(tryToSearchFeedbackTopics.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(tryToSearchFeedbackTopics.rejected, (state, action) => {
        state.searchLoading = false;
        const err = action.error as unknown as AxiosError;
        state.searchError = (err?.message as string) || 'Failed to fetch topics';
      })
      .addCase(tryToSearchFeedbackTopics.fulfilled, (state, action) => {
        state.searchLoading = false;
        const payload = action.payload as SearchResult;
        state.topics = payload?.data ?? [];
        state.total = payload?.total ?? 0;
        state.page = payload?.page ?? 1;
        state.size = payload?.size ?? 10;
      })

      // stats
      .addCase(tryToGetFeedbackTopicStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(tryToGetFeedbackTopicStats.rejected, (state, action) => {
        state.statsLoading = false;
        const err = action.error as unknown as AxiosError;
        state.statsError = (err?.message as string) || 'Failed to load stats';
      })
      .addCase(tryToGetFeedbackTopicStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload ?? null;
      })

      // exec by category
      .addCase(tryToFetchFeedbackExecByCategory.pending, (state) => {
        state.execByCategoryLoading = true;
        state.execByCategoryError = null;
      })
      .addCase(tryToFetchFeedbackExecByCategory.rejected, (state, action) => {
        state.execByCategoryLoading = false;
        state.execByCategoryError = (action.payload as string) || 'Failed to load category stats';
      })
      .addCase(tryToFetchFeedbackExecByCategory.fulfilled, (state, action) => {
        state.execByCategoryLoading = false;
        state.execByCategory = action.payload ?? null;
      });
  },
});

export default feedbackTopicsSlice.reducer;
